/**
 * Map Interaction Handlers for Native Interactive Site Map (ISM)
 *
 * Pure DOM/map-event delegation — no Redux, no CSS, no business logic. Translates raw
 * zoom/pan/map-type browser and Google Maps SDK events into the semantic callbacks
 * plugins/tracking.plugin.js consumes, including which input device drove the
 * interaction — information `zoom_changed`/`dragstart` don't carry on their own.
 *
 * @module nativeInteractiveSiteMap/handlers/mapInteractionHandlers
 */

const ZOOM_BUTTON_SELECTOR = 'button[title="Zoom in"], button[title="Zoom out"]';

function createMapInteractionHandlers() {
  const state = {
    lastInputMethod: 'mouse',
    lastZoom: null,
    domListeners: [],
    mapEventUnsubscribers: [],
  };

  function onWheel() {
    state.lastInputMethod = 'mouse';
  }

  function onTouchStart() {
    state.lastInputMethod = 'touch';
  }

  function onClick(event) {
    if (event.target.closest(ZOOM_BUTTON_SELECTOR)) {
      state.lastInputMethod = 'button';
    }
  }

  /**
   * @param {ReturnType<import('libraries/googleMapsAdapter').default>['createFacade']} mapFacade
   * @param {HTMLElement} mapElement - `[data-map-element]`, the map's container
   * @param {object} callbacks
   * @param {(payload: { interactionType: string, inputMethod: string }) => void} callbacks.onZoomInteraction
   * @param {(payload: { inputMethod: string }) => void} callbacks.onPanInteraction
   * @param {(payload: { mapType: string }) => void} callbacks.onMapTypeInteraction
   */
  function bind(mapFacade, mapElement, { onZoomInteraction, onPanInteraction, onMapTypeInteraction }) {
    state.lastZoom = mapFacade.getCameraSnapshot().zoom;

    // capture: true on all three — Google Maps' own zoom-button/wheel handling runs on an inner
    // element during the target phase and fires zoom_changed synchronously, so a bubble-phase
    // listener here would still be racing it on the very first tick (see NHS20-9089: the
    // zoom-button click fix already needed this; the wheel-scroll case had the same race).
    mapElement.addEventListener('wheel', onWheel, { passive: true, capture: true });
    mapElement.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    mapElement.addEventListener('click', onClick, true);
    state.domListeners.push(
      () => mapElement.removeEventListener('wheel', onWheel, true),
      () => mapElement.removeEventListener('touchstart', onTouchStart, true),
      () => mapElement.removeEventListener('click', onClick, true),
    );

    state.mapEventUnsubscribers.push(
      mapFacade.onMapEvent('zoom_changed', () => {
        const newZoom = mapFacade.getCameraSnapshot().zoom;
        const interactionType = newZoom > state.lastZoom ? 'zoom_in' : 'zoom_out';
        state.lastZoom = newZoom;
        onZoomInteraction({ interactionType, inputMethod: state.lastInputMethod });
      }),
      mapFacade.onMapEvent('dragstart', () => {
        // A drag can only originate from mouse or touch — a stale 'button' value (left over
        // from the last zoom-button click) is never a real pan source.
        const inputMethod = state.lastInputMethod === 'button' ? 'mouse' : state.lastInputMethod;
        onPanInteraction({ inputMethod });
      }),
      mapFacade.onMapEvent('maptypeid_changed', () => {
        const mapType = mapFacade.getMapType();
        // Our own Road/Satellite buttons are the only way to trigger this event (see module
        // doc above), so 'terrain'/'hybrid' should never occur — but report only what we
        // actually know how to name rather than silently mislabeling anything else 'satellite'.
        if (mapType !== 'roadmap' && mapType !== 'satellite') {
          return;
        }
        onMapTypeInteraction({ mapType: mapType === 'roadmap' ? 'road' : 'satellite' });
      }),
    );
  }

  function unbind() {
    state.domListeners.forEach((removeListener) => removeListener());
    state.domListeners = [];
    state.mapEventUnsubscribers.forEach((unsubscribe) => unsubscribe());
    state.mapEventUnsubscribers = [];
    state.lastZoom = null;
  }

  return { bind, unbind };
}

export default createMapInteractionHandlers;
